import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import "./DatePicker-Custom.css";
import {format} from 'date-fns';
import s from './BookNow.module.scss';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LoadingInside from "../../components/Loading/LoadingInside";

const REZDY_BASE_URL = process.env.REACT_APP_API_LOCAL_URL;

const BookNow = ({tour, plans}) => {
    const navigate = useNavigate();
    const [availability, setAvailability] = useState([]);
    const [product, setProduct] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [availableTimes, setAvailableTimes] = useState([]); // New: Times for selected date
    const [quantities, setQuantities] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [openParticipants, setOpenParticipants] = useState(false);
    const popupRef = useRef(null);
    const containerRef = useRef(null);
    const [showStickyBtn, setShowStickyBtn] = useState(true);

    const [minAllowedDate, setMinAllowedDate] = useState(null);
    useEffect(() => {
        if (availability.length > 0 && product?.minimumNoticeMinutes != null) {

            const minutes = Number(product.minimumNoticeMinutes);

            let earliest;

            if (!tour.last_min_available) {
                earliest = getMinDateOnly(minutes);
            } else {
                earliest = new Date(); // today allowed
                earliest.setHours(0, 0, 0, 0);
            }

            setMinAllowedDate(earliest);

        }
    }, [availability, product, tour]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError('');
            const productCode = tour.rezdy_id || tour.rezdyId || ''; // fallback

            if (!productCode) {
                setError('Product code is missing.');
                setLoading(false);
                return;
            }

            try {
                const startDate = new Date();

                const firstEndDate = new Date(
                    Date.now() + 180 * 24 * 60 * 60 * 1000
                );

                const secondEndDate = new Date(
                    Date.now() + 360 * 24 * 60 * 60 * 1000
                );

// First 180 days
                const firstRes = await axios.get(
                    `${REZDY_BASE_URL}availability`,
                    {
                        params: {
                            productCode,
                            startTime: format(startDate, 'yyyy-MM-dd'),
                            endTime: format(firstEndDate, 'yyyy-MM-dd'),
                        }
                    }
                );

// Next 180 days
                const secondRes = await axios.get(
                    `${REZDY_BASE_URL}availability`,
                    {
                        params: {
                            productCode,
                            startTime: format(firstEndDate, 'yyyy-MM-dd'),
                            endTime: format(secondEndDate, 'yyyy-MM-dd'),
                        }
                    }
                );

                const firstSessions =
                    firstRes.data.data?.availability?.sessions || [];

                const secondSessions =
                    secondRes.data.data?.availability?.sessions || [];

// merge both
                setAvailability([
                    ...firstSessions,
                    ...secondSessions
                ]);

                setProduct(
                    firstRes.data.data?.product?.product || []
                );
            } catch (err) {
                setError('空き状況の読み込みに失敗しました。もう一度お試しください。');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [tour]);

    // When date changes → filter available times for that date
    useEffect(() => {
        if (selectedDate) {
            const dateStr = format(selectedDate, 'yyyy-MM-dd');
            const times = availability.filter(s =>
                format(new Date(s.startTimeLocal), 'yyyy-MM-dd') === dateStr
            );
            setAvailableTimes(times);

            // Auto-select if only ONE time (silent)
            if (times.length === 1) {
                setSelectedSession(times[0]);
            } else {
                setSelectedSession(null);
            }
        } else {
            setAvailableTimes([]);
            setSelectedSession(null);
        }
    }, [selectedDate, availability]);

    useEffect(() => {
        setQuantities({});
    }, [selectedDate, selectedPlan]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setOpenParticipants(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    useEffect(() => {
        const handleScroll = () => {
            if (!containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();

            const isVisible =
                rect.top <= window.innerHeight && rect.bottom >= 0;

            setShowStickyBtn(!isVisible);
        };

        window.addEventListener('scroll', handleScroll);

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const getMinDateOnly = (minutes) => {
        const now = new Date();

        const cutoff = new Date(now.getTime() + minutes * 60 * 1000);

        // 👉 strip time completely
        cutoff.setHours(0, 0, 0, 0);

        return cutoff;
    };
    const getPlanPrices = () => {
        if (!selectedPlan || !selectedDate) return {};

        const now = new Date();
        const diffHours = (selectedDate - now) / (1000 * 60 * 60);

        const isEarlyBird = diffHours >= (tour.early_bird_time || 0);

        return {
            ad: isEarlyBird && selectedPlan.eb_ad_price ? selectedPlan.eb_ad_price : selectedPlan.ad_price,
            ch: isEarlyBird && selectedPlan.eb_ch_price ? selectedPlan.eb_ch_price : selectedPlan.ch_price,
            family: isEarlyBird && selectedPlan.eb_family_price ? selectedPlan.eb_family_price : selectedPlan.family_price,
            inf: isEarlyBird && selectedPlan.eb_inf_price ? selectedPlan.eb_inf_price : selectedPlan.inf_price,
        };
    };

    const handleQuantityChange = (optionId, delta) => {
        setQuantities(prev => ({
            ...prev,
            [optionId]: Math.max(0, (prev[optionId] || 0) + delta)
        }));
    };

    const handleContinue = () => {
        if (!selectedSession || totalParticipants === 0) {
            setError('日付、時間、少なくとも1名の参加者を選択してください。');
            return;
        }

        const bookingData = {
            selectedSession,
            quantities,
            totalParticipants,
            product,
        };
        console.log(bookingData)
        navigate('/booking-details', {state: bookingData});
    };

    if (loading) return <LoadingInside height={"40vh"}/>;


    const isLastMinute = () => {
        if (!selectedDate) return false;

        const now = new Date();
        const diffHours = (selectedDate - now) / (1000 * 60 * 60);

        return tour.last_min_available && diffHours <= tour.last_minit_time;
    };

    const getLastMinuteOption = () => {
        if (!isLastMinute() || !selectedPlan || !selectedSession) return null;

        const label = selectedPlan.last_min_price;

        return selectedSession.priceOptions.find(o => o.label === label);
    };

    const availableDates = availability
        .map(s => new Date(s.startTimeLocal))
        .filter(date => {
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);

            const min = new Date(minAllowedDate);
            min.setHours(0, 0, 0, 0);

            return d > min;
        });
    const normalizedMinDate = new Date(minAllowedDate);
    normalizedMinDate.setHours(0, 0, 0, 0);
    const getLabelType = (label) => {
        const prices = getPlanPrices();

        if (label === prices.ad) return 'Adult';
        if (label === prices.ch) return 'Child';
        if (label === prices.family) return 'Family';
        if (label === prices.inf) return 'Infant';

        return label;
    };
    const prices = getPlanPrices();
    const getOrderedOptions = () => {
        const prices = getPlanPrices();
        if (!selectedSession) return [];

        const order = [
            {key: 'Adult', label: prices.ad},
            {key: 'Child', label: prices.ch},
            {key: 'Infant', label: prices.inf},
            {key: 'Family', label: prices.family},
        ];

        return order
            .map(item => {
                const option = selectedSession.priceOptions.find(
                    o => o.label === item.label
                );

                if (!option) return null;

                // find matching plan (IMPORTANT PART)
                const plan = plans?.find(p =>
                    [p.ad_price, p.eb_ad_price, p.ch_price, p.eb_ch_price, p.family_price, p.eb_family_price, p.inf_price, p.eb_inf_price]
                        .includes(option.label)
                );

                return {
                    ...option,
                    displayName: item.key,
                    note:
                        item.key === 'Adult' ? plan?.adult_note :
                            item.key === 'Child' ? plan?.child_note :
                                item.key === 'Infant' ? plan?.infant_note :
                                    item.key === 'Family' ? plan?.family_note :
                                        ''
                };
            })
            .filter(Boolean);
    };

    const calculateTotalParticipants = () => {
        let total = 0;

        getOrderedOptions().forEach(option => {
            const qty = quantities[option.id] || 0;

            if (option.displayName === 'Family') {
                total += qty * 4; // 2 adult + 2 child
            } else if (option.displayName !== 'Infant') {
                total += qty;
            }
        });

        return total;
    };
    const totalParticipants = calculateTotalParticipants();
    const orderedOptions = getOrderedOptions();
    const noParticipantOptions =
        selectedSession &&
        selectedPlan &&
        orderedOptions.length === 0;
    const participantDisabled =
        !selectedSession || !selectedPlan || noParticipantOptions;
    console.log({selectedSession})
    console.log({prices})

    const getPricingMode = () => {
        if (!selectedDate) return null;

        const now = new Date();
        const diffHours = (selectedDate - now) / (1000 * 60 * 60);

        if (tour.last_min_available && diffHours <= tour.last_minit_time) {
            return 'last_min';
        }

        if (tour.eary_bird_available && diffHours >= tour.early_bird_time) {
            return 'early_bird';
        }

        return 'normal';
    };
    const pricingMode = getPricingMode();

    const getBasePrices = () => {
        if (!product?.priceOptions) return [];

        return product.priceOptions.map(p => ({
            label: p.label,
            price: p.price
        }));
    };
    const isEarlyBirdAvailable = () => {
        return tour.eary_bird_available === 1;
    };
    const mapLabel = (label) => {
        const plan = plans?.[0]; // use first plan as reference

        if (!plan) return label;

        if (label === plan.ad_price || label === plan.eb_ad_price) return 'Adult';
        if (label === plan.ch_price || label === plan.eb_ch_price) return 'Child';
        if (label === plan.family_price || label === plan.eb_family_price) return 'Family';
        if (label === plan.inf_price || label === plan.eb_inf_price) return 'Infant';

        return null;
    };
    const getDisplayPrices = () => {
        if (!plans?.length || !product?.priceOptions) return [];

        const isEB = isEarlyBirdAvailable();
        const ebHours = tour.early_bird_time; // e.g. 96

        const categories = [
            {
                name: '大人',
                normal: p => p.ad_price,
                early: p => p.eb_ad_price
            },
            {
                name: '小人',
                normal: p => p.ch_price,
                early: p => p.eb_ch_price
            },
            {
                name: '家族',
                normal: p => p.family_price,
                early: p => p.eb_family_price
            }
        ];

        return categories.map(cat => {
            let normalPrice = null;
            let earlyPrice = null;

            plans.forEach(plan => {
                const normalLabel = cat.normal(plan);
                const earlyLabel = cat.early(plan);

                const normalOption = product.priceOptions.find(p => p.label === normalLabel);
                const earlyOption = product.priceOptions.find(p => p.label === earlyLabel);

                if (normalOption) {
                    if (normalPrice === null || normalOption.price < normalPrice) {
                        normalPrice = normalOption.price;
                    }
                }

                if (isEB && earlyOption) {
                    if (earlyPrice === null || earlyOption.price < earlyPrice) {
                        earlyPrice = earlyOption.price;
                    }
                }
            });

            if (normalPrice === null) return null;

            return {
                name: cat.name,
                normalPrice,
                earlyPrice,
                ebHours
            };
        }).filter(Boolean);
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'AUD',
            minimumFractionDigits: 2
        }).format(price);
    };

    const formatDisplayPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'AUD',
            minimumFractionDigits: 0
        }).format(price);
    };

    const getParticipantSummary = () => {
        const items = getOrderedOptions()
            .map(option => {
                const qty = quantities[option.id] || 0;

                if (qty === 0) return null;

                return `${translateLabel(getLabelType(option.label))} x${qty}`;
            })
            .filter(Boolean);

        if (items.length === 0) return '参加者を選択';

        return items.join(', ');
    };

    const scrollToSelf = () => {
        const y = containerRef.current.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({top: y, behavior: 'smooth'});
    };
    const getFromPrice = () =>
        getDisplayPrices().find(p => p.name === '大人')?.price || null;

    const displayPrices = getDisplayPrices();
    if (displayPrices.length === 0) {
        return (
            <div className={` p-2 ${s.container}`}>
                <Alert severity="error">
                    現在このツアーは予約できません。料金情報が見つかりませんでした。
                </Alert>
            </div>
        );
    }
    const translateLabel = (label) => {
        const map = {
            Adult: "大人",
            Child: "子供",
            Infant: "幼児",
            Family: "ファミリー",
        };

        return map[label] || label;
    };
    const calculateTotalPrice = () => {
        let total = 0;

        orderedOptions.forEach(option => {
            const qty = quantities[option.id] || 0;
            total += option.price * qty;
        });

        // Add last minute fee if applicable
        if (isLastMinute() && getLastMinuteOption()) {
            total += getLastMinuteOption().price * calculateTotalParticipants();
        }

        return total;
    };
    const getEarlyBirdDeadline = () => {
        const now = new Date();
        return new Date(now.getTime() + (tour.early_bird_time * 60 * 60 * 1000));
    };
    const earlyBirdDeadline = getEarlyBirdDeadline();


    const getEarlyBirdInfo = () => {
        return {
            available: tour?.eary_bird_available === 1,
            hours: tour?.early_bird_time || 0
        };
    };
    const earlyBird = getEarlyBirdInfo();

    const formattedEarlyBirdDeadline = format(
        earlyBirdDeadline,
        'MM/dd'
    );
    return (
        <div ref={containerRef} className="min-h-screen bg-gray-100 py-10">
            <div className={s.container}>

                {error && <Alert severity="error" className="mb-4">{error}</Alert>}

                <div className={s.bookingCard}>

                    {/* TITLE */}
                    {/*<h3 className={s.title}>予約する</h3>*/}

                    {/* PRICE SUMMARY */}
                    {product?.priceOptions && (
                        <div className={s.priceSummary}>


                            <div className={s.priceList}>
                                {earlyBird.available && (
                                    <div className={s.priceItem}>
                                        <strong> {formattedEarlyBirdDeadline}以降なら早割{earlyBird.hours}適用！</strong>
                                    </div>
                                )}
                                {getDisplayPrices().map((item, idx) => (
                                    <div key={idx} className={s.priceItem}>
                                        <div className={s.priceLabel}>{item.name} </div>
                                        <div className={s.priceLabelItems}>
                                            <strong>
                                                {formatPrice(item.normalPrice)}
                                            </strong>

                                            {item.earlyPrice && (
                                                <span className={s.priceCategoryEarlyBird}>
                                                  (早割料金:{formatPrice(item.earlyPrice)})

    </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <label>【前日の空き状況確認】LINEからお気軽にお問合せを！</label>
                    {/* DATE */}
                    <div className={s.section}>

                        <DatePicker
                            selected={selectedDate}
                            onChange={(date) => {
                                setSelectedDate(date);
                                setSelectedSession(null);
                            }}
                            includeDates={availableDates}
                            minDate={normalizedMinDate}
                            className={s.input}
                            placeholderText="日付を選択"
                        />
                    </div>

                    {/* TIME */}
                    {selectedDate && (
                        <div className={s.section}>
                            {/*<label>時間</label>*/}

                            {availableTimes.length === 0 ? (
                                <span className={s.empty}>利用可能な時間なし</span>
                            ) : availableTimes.length === 1 ? (
                                <select
                                    className={s.select}
                                    value={selectedPlan?.id || ''}
                                    onChange={(e) => {
                                        const plan = plans.find(p => p.id === Number(e.target.value));
                                        setSelectedPlan(plan);
                                    }}
                                >
                                    <option value="">プランを選択してください</option>
                                    {plans.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.campaign}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <select
                                    className={s.input}
                                    value={selectedSession?.startTimeLocal || ''}
                                    onChange={(e) => {
                                        const time = availableTimes.find(
                                            s => s.startTimeLocal === e.target.value
                                        );
                                        setSelectedSession(time || null);
                                    }}
                                >
                                    <option value="">時間を選択</option>
                                    {availableTimes.map((session) => (
                                        <option key={session.startTimeLocal} value={session.startTimeLocal}>
                                            {format(new Date(session.startTimeLocal), 'h:mm a')} -
                                            {format(new Date(session.endTimeLocal), 'h:mm a')}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}
                    {/* PRICING MODE MESSAGE */}
                    {selectedDate && pricingMode && (
                        <div className={s.pricingMessage}>
                            {pricingMode === 'last_min' && '⚠️ 直前価格'}
                            {pricingMode === 'early_bird' && '🎉 早割適用'}
                            {pricingMode === 'normal' && '通常料金'}
                        </div>
                    )}
                    {/* PARTICIPANTS */}
                    <div className={s.section} ref={popupRef}>

                        {/* PLACEHOLDER (summary + click) */}
                        <div
                            className={`${s.placeholder} ${participantDisabled ? s.disabled : ''}`}
                            onClick={() => {
                                if (!participantDisabled) {
                                    setOpenParticipants(prev => !prev);
                                }
                            }}
                        >
                            <div>
                                <div className={s.placeholderTitle}>
                                    参加人数
                                </div>

                                <div className={s.placeholderSub}>
                                    {getParticipantSummary()}
                                </div>
                            </div>

                            <div className={s.arrow}>
                                ▼
                            </div>
                        </div>
                        {selectedPlan && noParticipantOptions && (
                            <div className={s.errorMsg}>
                                参加可能な料金オプションがありません。
                            </div>
                        )}
                        {/* POPUP */}
                        {openParticipants && selectedSession && (
                            <div className={s.popup} onClick={(e) => e.stopPropagation()}>
                                <label>参加人数</label>

                                <div className={s.participantsBox}>
                                    {orderedOptions.map(option => (
                                        <div key={option.id}>
                                            <div className={s.participantRow}>

                                                <div>
                                                    <div className={s.label}>
                                                        {translateLabel(option.displayName)}
                                                    </div>

                                                    {/*<div className={s.price}>*/}
                                                    {/*    {formatPrice(option.price)}*/}
                                                    {/*</div>*/}

                                                    <div className={"d-inline-flex"}>
                                                        <div className={s.price}>
                                                            {formatPrice(option.price)}
                                                        </div>
                                                        {(quantities[option.id] || 0) > 0 && (
                                                            <div className={s.price}>
                                                                × {quantities[option.id]} ={" "}
                                                                <strong>
                                                                    {formatPrice(option.price * quantities[option.id])}
                                                                </strong>
                                                            </div>
                                                        )}
                                                    </div>


                                                </div>

                                                <div className={s.counter}>
                                                    <button onClick={() => handleQuantityChange(option.id, -1)}>-
                                                    </button>
                                                    <span>{quantities[option.id] || 0}</span>
                                                    <button onClick={() => handleQuantityChange(option.id, 1)}>+
                                                    </button>
                                                </div>

                                            </div>
                                            {/* ✅ Note under price */}
                                            {option.note && (
                                                <div className={s.note}>
                                                    {option.note}
                                                </div>


                                            )}
                                            <div className={s.space}>

                                            </div>
                                        </div>
                                    ))}

                                    {isLastMinute() && getLastMinuteOption() ? (
                                        <div className={s.lastMinute}>
                                            ⚠️
                                            直前予約手数料: {formatPrice(getLastMinuteOption().price)} * {calculateTotalParticipants()}
                                        </div>
                                    ) : <></>}
                                </div>
                                <div className={s.totalPrice}>
                                    合計金額: <strong>{formatPrice(calculateTotalPrice())}</strong>
                                </div>

                                {/* CLOSE BUTTON */}
                                <div className={s.closeBtn}>
                                    <button onClick={() => setOpenParticipants(false)}>
                                        次へ進む
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {(!openParticipants) && <div className={s.totalPrice}>
                        合計金額: <strong>{formatPrice(calculateTotalPrice())}</strong>
                    </div>}
                    {/* CTA */}
                    <Button
                        className={s.booknowBtn}
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={handleContinue}
                        disabled={!selectedSession || totalParticipants === 0 || openParticipants}
                    >
                        予約詳細へ進む
                    </Button>

                </div>
            </div>
            {showStickyBtn && (
                <div className={s.stickyBar}>
                    <div
                        className={s.stickyBtn}
                        onClick={scrollToSelf}
                    >
                        <div className={s.left}>
                            <div className={s.price}>
                                From <span> {formatPrice(getFromPrice())}</span>
                            </div>

                        </div>

                        <div className={s.right}>
                            <div className={s.booknow}>
                                今すぐ予約
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookNow;